import { FormBase } from "../../util/form-base-form";
import { Roles } from "../../constants/roles";

export type DashboardGranularity = "daily" | "monthly";

export class DashboardForm extends FormBase {
    private readonly root: HTMLElement;

    public constructor(root: HTMLElement) {
        super();
        this.root = root;
    }

    public static getRoots(scope: Document | HTMLElement | ParentNode = document): HTMLElement[] {
        const selector = `[data-role="${Roles.ADMIN_DASHBOARD_ROOT}"]`;
        const roots: HTMLElement[] = [];
        if (scope instanceof HTMLElement && scope.matches(selector)) {
            roots.push(scope);
        }
        return roots.concat(this.prototype.queryElements.call(this.prototype, scope, selector, HTMLElement));
    }

    public getRoot(): HTMLElement {
        return this.root;
    }

    public isInitialized(): boolean {
        return this.root.dataset.adminDashboardInit === "1";
    }

    public markInitialized(): void {
        this.root.dataset.adminDashboardInit = "1";
    }

    public getStatusEl(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${Roles.ADMIN_DASHBOARD_STATUS}"]`, HTMLElement);
    }

    public getGranularityButtons(): HTMLButtonElement[] {
        return this.queryElements(this.root, "[data-granularity]", HTMLButtonElement);
    }

    public getChartCanvases(): HTMLCanvasElement[] {
        return this.queryElements(this.root, '[data-role="admin-dashboard-chart"]', HTMLCanvasElement);
    }

    public getStatEls(): HTMLElement[] {
        return this.queryElements(this.root, '[data-role="admin-dashboard-stat"]', HTMLElement);
    }

    public getDiffEls(): HTMLElement[] {
        return this.queryElements(this.root, '[data-role="admin-dashboard-diff"]', HTMLElement);
    }

    public getStatusLinksLists(): HTMLElement[] {
        return this.queryElements(this.root, '[data-role="admin-dashboard-status-links"]', HTMLElement);
    }

    public getRankingMonthContainers(): HTMLElement[] {
        return this.queryElements(this.root, '[data-role="admin-dashboard-ranking-months"]', HTMLElement);
    }

    public getGranularityButtonFromEvent(event: Event): HTMLButtonElement | null {
        return this.closest(event, "[data-granularity]", HTMLButtonElement);
    }

    public getTabButtons(): HTMLButtonElement[] {
        return this.queryElements(this.root, "[data-dashboard-tab]", HTMLButtonElement);
    }

    public getSectionPanels(): HTMLElement[] {
        return this.queryElements(this.root, "[data-dashboard-section]", HTMLElement);
    }

    public getTabButtonFromEvent(event: Event): HTMLButtonElement | null {
        return this.closest(event, "[data-dashboard-tab]", HTMLButtonElement);
    }

    // Pure UI state (no data refetch) — switches which single section panel
    // is laid out "active", the rest fall back to &--inactive (see
    // _dashboard.blade.scss for why that's visibility:hidden, not display:none).
    public activateTab(key: string): void {
        for (const button of this.getTabButtons()) {
            const isActive = button.dataset.dashboardTab === key;
            button.classList.toggle("is-active", isActive);
            button.setAttribute("aria-selected", isActive ? "true" : "false");
        }
        for (const panel of this.getSectionPanels()) {
            panel.classList.toggle("admin-dashboard__section--inactive", panel.dataset.dashboardSection !== key);
        }
    }

    public getChartLegendElement(canvas: HTMLCanvasElement): HTMLElement | null {
        const box = this.closest(canvas, ".admin-dashboard__chart-box", HTMLElement);
        return box ? this.queryElement(box, '[data-role="admin-dashboard-chart-legend"]', HTMLElement) : null;
    }

    public getChartLegendButtonFromEvent(event: Event): HTMLButtonElement | null {
        return this.closest(event, ".admin-dashboard__chart-legend-btn", HTMLButtonElement);
    }
}
